// The prime district round the Commons (plazmi.district): three rings of
// quarters to the north and west of the old island, along tree-lined lanes,
// with little squares and signposts, and (ring 4) the far district out past
// the Edge Walk, never sold for coins. Plots open for sale by quarter (policy
// :open-quarters, first a block in the far corner): mown lots with a FOR SALE
// sign and an orange pennant that shows from any distance. The rest are the
// crown's reserve: staked lots gone to wildflowers, with a RESERVED sign. The shape comes once from
// /api/district; which plots are open, and the owned ones in full, come with
// /api/commons.
//
// Five hundred plots stay cheap: every stake, string, sign, hedge, flower,
// tree and lamp is one instanced mesh per kind, and the ground and paths are
// merged. An owned plot is built in full (its pavilion, its garden) only when
// it looks big enough on screen to read; smaller, the same plot baked into a few
// meshes (farplot.ts).
import * as THREE from 'three';
import { Q } from './quality.js';
import { mat, cyl, at, finishShadows } from './kit.js';
import { island } from './stage.js';
import { wbox, buildHouse } from './house.js';
import { surf } from './surfaces.js';
import * as Y from './yard.js';
import * as Fu from './furnish.js';
import { fountain } from './scenes.js';
import { flagpole } from './parts.js';
import { PLAZMI_FLAG } from './pixel.js';
import { letters, mergeStatic } from './place.js';
import { primeExtras, splitMaterials, plotSignLines, plotHover } from './town.js';
import { rng } from './util.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { applyView, chunkInstanced, FarDetail } from './viewdist.js';
import { Atlas } from './lands.js';
import { bakeFar, farGlow } from './farplot.js';

type Rect4 = [number, number, number, number];
export type DPlot = { n: number; ring: number; quarter: string; size: [number, number]; face: 'n' | 's' | 'e' | 'w'; rect: Rect4; where: string };
export type Layout = {
  quarters: { id: string; name: string; ring: number; side: string; inner: boolean; rect: Rect4 }[];
  grounds: Rect4[]; avenues: { name: string; rect: Rect4; kind: string }[]; lanes: { quarter: string; rect: Rect4 }[];
  squares: { id: string; quarter: string; kind: string; name: string; rect: Rect4; at: [number, number] }[];
  crossings: { at: [number, number]; walk: string; ring: number }[];
  signs: { at: [number, number]; text: string; side: string; dir: number }[];
  trees: [number, number, string][]; lamps: [number, number][]; plots: DPlot[];
};
export type PlotsLive = {
  prices: { core: number; inner: number; outer: number }; open: { core: number; inner: number; outer: number };
  first: { inner: number; outer: number }; counts?: Record<string, number>;
  // the quarters open for sale by area (only those really for coins), and where they are in words;
  // glow land (kept for gifts from humans, or the crown's reserve), never for coins
  open_quarters?: string[]; glow_quarters?: string[]; open_line?: string | null;
  plots: { n: number; status: string; owner?: string; owner_name?: string; yard?: Fu.Thing[]; pavilion?: any; designs?: Fu.Design[];
    name?: string; sign?: string; kind?: string; kind_name?: string; private?: boolean; page?: string }[];
};

const FACE: Record<string, number> = { s: 0, n: Math.PI, e: Math.PI / 2, w: -Math.PI / 2 };
// an owned plot's building is drawn in full while it looks at least this many pixels across (on High)
const NEAR_PX = 70, NEAR_BAND = 1.15;
const RESERVED = '#7d6fd1', FOR_SALE = '#e8803a';

export async function loadDistrict(): Promise<Layout | null> {
  // (?nodistrict draws the old island alone, to measure against)
  if (new URLSearchParams(location.search).has('nodistrict')) return null;
  try { const r = await fetch('/api/district'); return r.ok ? await r.json() : null; } catch { return null; }
}

// a plot's frame: local (0..w, 0..d), front at z = d, set on its spot and turned to face its lane
function frame(p: DPlot) {
  const [x0, z0, x1, z1] = p.rect, [w, d] = p.size;
  return new THREE.Matrix4().makeTranslation((x0 + x1) / 2, 0, (z0 + z1) / 2)
    .multiply(new THREE.Matrix4().makeRotationY(FACE[p.face]))
    .multiply(new THREE.Matrix4().makeTranslation(-w / 2, 0, -d / 2));
}

// an instanced mesh from a list of matrices (and colours)
function inst(geo: THREE.BufferGeometry, m: THREE.Material, ms: THREE.Matrix4[], cols?: THREE.Color[], o: { shadow?: boolean; noCollide?: boolean } = {}) {
  const im = new THREE.InstancedMesh(geo, m, Math.max(1, ms.length));
  ms.forEach((x, i) => im.setMatrixAt(i, x));
  im.count = ms.length;
  if (cols) cols.forEach((c, i) => im.setColorAt(i, c));
  im.castShadow = !!o.shadow; im.receiveShadow = true;
  if (!o.shadow) im.userData.noShadow = true;
  if (o.noCollide) im.userData.noCollide = true;
  im.computeBoundingSphere();
  return im;
}
const M = (x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, ry = 0) =>
  new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), ry), new THREE.Vector3(sx, sy, sz));

// a painted board on a post, for a quarter's name; its face drawn once per name
const boardMats = new Map<string, THREE.Material>();
function boardMat(text: string, small = false) {
  const k = text + small;
  if (!boardMats.has(k)) boardMats.set(k, mat('#ffffff', { map: letters([{ text, size: small ? 54 : 64, color: '#3a2a20', font: 'Georgia, serif', weight: 600 }], 640, 128, '#f6e9c8'), rough: 0.9, tint: '#e9e1d2' }));
  return boardMats.get(k)!;
}
// (P83) every fingerpost's words in one atlas, so the district's ~30 boards are one material
// (merged into a few meshes by cell) instead of a texture and a draw call each
let signAtlas: Atlas | null = null, signMat: THREE.Material | null = null;
const signUV = new Map<string, [number, number, number, number]>();
const ATLAS_CELLS = 4 * 10;
function boardFace(text: string, small = false) {
  const k = text + small;
  if (!signAtlas) {
    signAtlas = new Atlas(512, 100, 4, 10, '#f6e9c8', '#3a2a20', '600 50px Georgia, serif');
    signMat = mat('#ffffff', { map: signAtlas.tex, rough: 0.9, tint: '#e9e1d2' });
  }
  if (!signUV.has(k)) {
    if (signUV.size >= ATLAS_CELLS) return new THREE.Mesh(new THREE.PlaneGeometry(1.62, 0.3), boardMat(text, small));
    signUV.set(k, signAtlas.add(text, small ? { font: '600 42px Georgia, serif' } : {}));
  }
  const uv = signUV.get(k)!, g = new THREE.PlaneGeometry(1.62, 0.3), a = g.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < a.count; i++) a.setXY(i, a.getX(i) ? uv[2] : uv[0], a.getY(i) ? uv[3] : uv[1]);
  return new THREE.Mesh(g, signMat!);
}
function fingerpost(arms: { text: string; ry: number; small?: boolean }[]) {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
  g.add(at(cyl(0.07, 0.08, 2.6, wood, 8), 0, 1.3, 0));
  g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.11, 10, 8), mat('#d6b05a', { rough: 0.4, metal: 0.5 })), 0, 2.66, 0));
  arms.forEach((a, i) => {
    const arm = new THREE.Group();
    arm.add(wbox(0.05, -0.17, -0.04, 1.75, 0.17, 0.04, wood));
    for (const s of [1, -1]) {
      const face = boardFace(a.text, a.small);
      face.position.set(0.9, 0, 0.042 * s); if (s < 0) face.rotation.y = Math.PI;
      arm.add(face);
    }
    arm.position.y = 2.2 - i * 0.42; arm.rotation.y = a.ry;
    g.add(arm);
  });
  return g;
}

// a soft, lumpy ball for tree crowns and shrubs, pale so each instance's colour tints it
const blobs: Record<number, THREE.BufferGeometry> = {};
function blobGeo(detail = 2) {
  if (blobs[detail]) return blobs[detail];
  const geo = new THREE.IcosahedronGeometry(1, detail), pos = geo.attributes.position, v = new THREE.Vector3(), c = new THREE.Color();
  const cols = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const n = Math.sin(v.x * 5.1 + 1.3) * 0.5 + Math.sin(v.y * 6.7 + 0.4) * 0.35 + Math.sin(v.z * 5.9 + 2.2) * 0.4;
    v.multiplyScalar(1 + 0.09 * n);
    if (v.y < -0.5) v.y = -0.5 + (v.y + 0.5) * 0.4;
    pos.setXYZ(i, v.x, v.y, v.z);
    c.setScalar(0.86 + v.y * 0.12 + n * 0.04);
    cols[i * 3] = c.r; cols[i * 3 + 1] = c.g; cols[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(cols, 3));
  geo.computeVertexNormals();
  return (blobs[detail] = geo);
}

// a flower bed, light: soil, a stone edge, and leaves and flowers merged into one mesh
// (yard.ts's flowerBed is finer, for a garden seen up close; a square has long borders)
const bedM = new THREE.MeshStandardMaterial({ roughness: 0.85, vertexColors: true, flatShading: true });
function bed(w: number, d: number, colors: string[], seed: number) {
  const g = new THREE.Group(), r = rng(seed * 11 + 5);
  g.add(wbox(-w / 2, 0, -d / 2, w / 2, 0.1, d / 2, mat('#4a3626', { rough: 1 })));
  const edge = mat('#b5a48c', { rough: 0.9 });
  g.add(wbox(-w / 2 - 0.06, 0, -d / 2 - 0.06, w / 2 + 0.06, 0.14, -d / 2, edge), wbox(-w / 2 - 0.06, 0, d / 2, w / 2 + 0.06, 0.14, d / 2 + 0.06, edge));
  g.add(wbox(-w / 2 - 0.06, 0, -d / 2, -w / 2, 0.14, d / 2, edge), wbox(w / 2, 0, -d / 2, w / 2 + 0.06, 0.14, d / 2, edge));
  const geos: THREE.BufferGeometry[] = [], greens = ['#4f7a3d', '#6b9447'];
  const piece = (rad: number, col: string, x: number, y: number, z: number) => {
    const p = new THREE.IcosahedronGeometry(rad, 0); p.translate(x, y, z);
    const c = new THREE.Color(col), a = new Float32Array(p.attributes.position.count * 3);
    for (let i = 0; i < a.length; i += 3) { a[i] = c.r; a[i + 1] = c.g; a[i + 2] = c.b; }
    p.setAttribute('color', new THREE.BufferAttribute(a, 3)); geos.push(p);
  };
  for (let i = 0, n = Math.floor(w * d * 9); i < n; i++) {
    const x = (r() - 0.5) * (w - 0.15), z = (r() - 0.5) * (d - 0.15);
    piece(0.08 + r() * 0.04, greens[i % 2], x, 0.16 + r() * 0.06, z);
    if (r() < 0.8) piece(0.05 + r() * 0.03, colors[Math.floor(r() * colors.length)], x + (r() - 0.5) * 0.06, 0.26 + r() * 0.1, z);
  }
  const m = new THREE.Mesh(mergeGeometries(geos), bedM); m.userData.noShadow = true; g.add(m);
  for (const p of geos) p.dispose();
  return g;
}

// ---------------------------------------------------------------------------
// what stands in a square

function squareFeature(kind: string, seed: number) {
  const g = new THREE.Group();
  const stone = mat('#c9c0b0', { rough: 0.9 }), wood = mat('#7a5638', { rough: 0.8 }), roofM = mat('#b5523b', { rough: 0.7 });
  switch (kind) {
    case 'well': {
      g.add(at(cyl(1.0, 1.05, 0.8, stone, 18), 0, 0.4, 0));
      g.add(at(cyl(0.82, 0.82, 0.05, mat('#3d6a8a', { rough: 0.2 }), 18), 0, 0.72, 0));
      for (const s of [-1, 1]) g.add(at(cyl(0.07, 0.07, 2.0, wood, 6), s * 0.9, 1.0, 0));
      const r = new THREE.Mesh(new THREE.ConeGeometry(1.45, 0.8, 4), roofM); r.position.y = 2.3; r.rotation.y = Math.PI / 4; g.add(r);
      break;
    }
    case 'fountain': { const f = fountain(); f.scale.setScalar(0.75); g.add(f); break; }
    case 'bandstand': {
      g.add(at(cyl(2.4, 2.5, 0.45, stone, 8), 0, 0.22, 0));
      for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; g.add(at(cyl(0.07, 0.07, 2.4, mat('#f6f1e6'), 6), Math.cos(a) * 2.1, 1.6, Math.sin(a) * 2.1)); }
      const r = new THREE.Mesh(new THREE.ConeGeometry(2.9, 1.3, 8), mat('#3f8f8a', { rough: 0.5 })); r.position.y = 3.4; g.add(r);
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), mat('#d6b05a', { rough: 0.4, metal: 0.5 })), 0, 4.1, 0));
      break;
    }
    case 'sundial': {
      g.add(at(cyl(0.35, 0.45, 1.0, stone, 12), 0, 0.5, 0));
      g.add(at(cyl(0.55, 0.55, 0.06, mat('#d6b05a', { rough: 0.4, metal: 0.5 }), 24), 0, 1.03, 0));
      const gn = new THREE.Mesh(new THREE.ConeGeometry(0.25, 0.4, 3), mat('#8a6a3a', { metal: 0.5, rough: 0.4 })); gn.position.set(0, 1.25, 0); gn.rotation.z = 0.5; g.add(gn);
      g.add(at(bed(3.2, 0.7, ['#e48aa0', '#f6f0e6', '#b98ae8'], seed), 0, 0, 1.8));
      break;
    }
    case 'pond': {
      const w = new THREE.Mesh(new THREE.CircleGeometry(2.3, 28), mat('#5f9fbd', { rough: 0.15, env: 1.2 })); w.rotation.x = -Math.PI / 2; w.position.y = 0.05; g.add(w);
      const r = rng(seed);
      for (let i = 0; i < 16; i++) { const a = (i / 16) * Math.PI * 2; const s = new THREE.Mesh(new THREE.SphereGeometry(0.32 + r() * 0.12, 7, 5), stone); s.scale.y = 0.5; s.position.set(Math.cos(a) * 2.45, 0.08, Math.sin(a) * 2.45); g.add(s); }
      for (let i = 0; i < 5; i++) { const pad = new THREE.Mesh(new THREE.CircleGeometry(0.22, 10), mat('#5a8a48')); pad.rotation.x = -Math.PI / 2; pad.position.set((r() - 0.5) * 2.4, 0.07, (r() - 0.5) * 2.4); g.add(pad); }
      break;
    }
    case 'maypole': {
      g.add(at(cyl(0.09, 0.11, 5.2, mat('#f6f1e6'), 8), 0, 2.6, 0));
      g.add(at(new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8), mat('#d6b05a', { metal: 0.5, rough: 0.4 })), 0, 5.3, 0));
      const cols = ['#e86a92', '#f2c14e', '#5fb3d9', '#8fcf6a', '#b98ae8', '#f08f5a'];
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2, x = Math.cos(a) * 2.2, z = Math.sin(a) * 2.2;
        const len = Math.hypot(2.2, 4.4);
        const rib = cyl(0.025, 0.025, len, mat(cols[i]), 4);
        rib.position.set(x / 2, 0.75 + 4.4 / 2, z / 2);
        rib.lookAt(new THREE.Vector3(x, 0.75, z)); rib.rotateX(Math.PI / 2);
        g.add(rib);
        g.add(at(cyl(0.05, 0.05, 0.75, wood, 5), x, 0.37, z));
      }
      break;
    }
    case 'oak': {
      g.add(at(Y.tree({ kind: 'oak', h: 7, seed } as any), 0, 0, 0));
      for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; const b = wbox(-0.5, 0.35, -0.18, 0.5, 0.45, 0.18, wood); b.position.set(Math.cos(a) * 1.6, 0, Math.sin(a) * 1.6); b.rotation.y = -a + Math.PI / 2; g.add(b); }
      break;
    }
    case 'flowers': {
      g.add(at(cyl(1.9, 1.9, 0.3, mat('#8b6a4e', { rough: 1 }), 24), 0, 0.15, 0));
      const r = rng(seed), cols = ['#e25d6a', '#f2c14e', '#f6f0e6', '#7aa7d9', '#e48aa0'];
      for (let i = 0; i < 46; i++) { const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 1.75; const f = new THREE.Mesh(new THREE.SphereGeometry(0.12, 6, 4), mat(cols[i % cols.length])); f.position.set(Math.cos(a) * d, 0.42 + r() * 0.12, Math.sin(a) * d); g.add(f); }
      g.add(at(Y.bush({ r: 0.5, seed } as any), 0, 0.3, 0));
      break;
    }
    case 'dovecote': {
      g.add(at(cyl(0.1, 0.12, 3.0, wood, 8), 0, 1.5, 0));
      g.add(wbox(-0.55, 3.0, -0.55, 0.55, 3.8, 0.55, mat('#f6f1e6')));
      const r = new THREE.Mesh(new THREE.ConeGeometry(0.95, 0.7, 4), roofM); r.position.y = 4.15; r.rotation.y = Math.PI / 4; g.add(r);
      for (const [x, z] of [[0, 0.56], [0.56, 0], [0, -0.56], [-0.56, 0]]) { const h = new THREE.Mesh(new THREE.CircleGeometry(0.12, 10), mat('#2a2220')); h.position.set(x, 3.45, z); h.lookAt(x * 3, 3.45, z * 3); g.add(h); }
      break;
    }
    default: { g.add(Y.birdbath()); g.add(at(bed(3.0, 0.7, ['#f2c14e', '#f6f0e6', '#e48aa0'], seed), 0, 0, 1.6)); }
  }
  return g;
}

// a tall two-sided board on a square: PLOTS FOR SALE, the quarter, the price
function saleBoard(name: string, price: number, [x, z]: [number, number]) {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
  const face = mat('#ffffff', { map: letters([{ text: 'PLOTS FOR SALE', size: 92, color: '#c8547e' }, { text: name, size: 70, color: '#3a2a20' }, { text: `from ${price} coins · buy plot <n>`, size: 44, color: '#6b4a26' }], 1024, 384, '#fbf5e8'), rough: 0.9 });
  for (const sx of [-3.4, 3.4]) g.add(at(cyl(0.16, 0.2, 7.4, wood, 8), sx, 3.7, 0));
  g.add(wbox(-3.8, 4.1, -0.12, 3.8, 7.0, 0.12, wood));
  for (const s of [1, -1]) {
    const f = new THREE.Mesh(new THREE.PlaneGeometry(7.3, 2.74), face);
    f.position.set(0, 5.55, 0.13 * s); if (s < 0) f.rotation.y = Math.PI;
    g.add(f);
  }
  // pennants along the top, in the for-sale orange
  const flagM = new THREE.MeshStandardMaterial({ color: FOR_SALE, emissive: FOR_SALE, emissiveIntensity: 0.35, roughness: 0.8, side: THREE.DoubleSide });
  for (let i = 0; i < 7; i++) { const t = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.7, 3), flagM); t.rotation.z = Math.PI; t.position.set(-3.3 + i * 1.1, 6.75, 0); g.add(t); }
  // turned to face the way visitors first look in (from the south-west)
  g.position.set(x, 0, z + 4.6); g.rotation.y = Math.atan2(-0.5, 1.0);
  g.userData.noCollide = true;
  return g;
}

// ---------------------------------------------------------------------------

export class District {
  root = new THREE.Group();
  layout: Layout;
  private lots = new THREE.Group();
  private owned = new THREE.Group();
  private ownedState = new Map<number, { near: THREE.Object3D | null; far: THREE.Object3D; data: any; at: THREE.Vector3; size: number; shown: boolean }>();
  private lotsKey = '';
  private grid = new Map<string, DPlot[]>();
  private byN = new Map<number, DPlot>();
  live: PlotsLive | null = null;
  lampSpots: [number, number, number][] = [];

  constructor(layout: Layout) {
    this.layout = layout;
    this.root.userData.keep = true;
    for (const p of layout.plots) {
      this.byN.set(p.n, p);
      const [x0, z0, x1, z1] = p.rect;
      for (let gx = Math.floor(x0 / 16); gx <= Math.floor(x1 / 16); gx++) for (let gz = Math.floor(z0 / 16); gz <= Math.floor(z1 / 16); gz++) {
        const k = `${gx},${gz}`; if (!this.grid.has(k)) this.grid.set(k, []); this.grid.get(k)!.push(p);
      }
    }
    const still = new THREE.Group();
    this.buildGround(still);
    this.buildPaths(still);
    this.buildSquares(still);
    this.buildSigns(still);
    finishShadows(still);
    // (P83) merged by cells, so a cell beyond the view distance or off screen isn't drawn (viewdist.ts)
    mergeStatic(still, { cell: 96 });
    this.root.add(still);
    this.buildTrees();
    this.buildLamps();
    this.buildHedges();
    this.lots.userData.keep = true; this.owned.userData.keep = true;
    this.root.add(this.lots, this.owned);
    chunkInstanced(this.root);
    this.farDetail.add(this.root);
    // (for the screenshot tools: the owned plots, and a way to hold them all near or all far)
    (window as any).__district = this;
  }
  lodForce: 'near' | 'far' | null = null;

  plot(n: number) { return this.byN.get(n) || null; }
  centre(n: number): [number, number] | null { const p = this.byN.get(n); if (!p) return null; const [x0, z0, x1, z1] = p.rect; return [(x0 + x1) / 2, (z0 + z1) / 2]; }
  quarterName(id: string) { return this.layout.quarters.find((q) => q.id === id)?.name || id; }
  quarterIndex(id: string) { return Math.max(0, this.layout.quarters.findIndex((q) => q.id === id)); }
  plotAt(x: number, z: number) {
    for (const p of this.grid.get(`${Math.floor(x / 16)},${Math.floor(z / 16)}`) || []) {
      const [x0, z0, x1, z1] = p.rect; if (x >= x0 && x <= x1 && z >= z0 && z <= z1) return p;
    }
    return null;
  }
  status(n: number): string {
    const L = this.live, p = this.byN.get(n);
    if (!L || !p) return 'reserved';
    const known = L.plots.find((x) => x.n === n);
    if (known) return known.status;
    if (p.ring === 0) return n <= L.open.core ? 'for-sale' : 'reserved';
    // the far district (ring 4, past the Edge Walk) is never sold for coins: reserved unless the server says otherwise
    if (p.ring >= 4) return 'reserved';
    // glow land is never sold for coins, whatever the number-order levers reach: unowned, it looks reserved
    if (L.glow_quarters?.includes(p.quarter)) return 'reserved';
    if (L.open_quarters?.includes(p.quarter)) return 'for-sale';
    if (p.ring < 3) return n - L.first.inner < L.open.inner ? 'for-sale' : 'reserved';
    return n - L.first.outer < L.open.outer ? 'for-sale' : 'reserved';
  }
  price(n: number) { const p = this.byN.get(n), L = this.live; if (!p || !L) return 0; return p.ring === 0 ? L.prices.core : p.ring < 3 ? L.prices.inner : L.prices.outer; }
  hoverText(p: DPlot) {
    const st = this.status(p.n), own = this.live?.plots.find((x) => x.n === p.n);
    const q = this.quarterName(p.quarter);
    if (st === 'owned' && own) return `${plotHover(own)} · ${q}`;
    return `Plot ${p.n} · ${q} · ${p.size[0]} × ${p.size[1]} · ${st === 'for-sale' ? `for sale, ${this.price(p.n)} coins` : 'Reserved'}`;
  }
  // an owned plot's page (inside its building), or null
  pageOf(p: DPlot) { return this.status(p.n) === 'owned' ? `/plot/${p.n}` : null; }
  // (P88) the plot under the pointer: where the ray first meets an owned plot's building or garden,
  // else where it meets the ground. Picking by the ground alone named the plot behind a building.
  pick(ray: THREE.Raycaster) {
    const hit = ray.intersectObject(this.owned, true)[0];
    if (hit) { const p = this.plotAt(hit.point.x, hit.point.z); if (p) return p; }
    const g = new THREE.Vector3();
    return ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), g) ? this.plotAt(g.x, g.z) : null;
  }
  // (P88) a strip at each owned building's door, on the Commons, for walking in (walk.ts triggers)
  doors(): { n: number; rect: { x0: number; z0: number; x1: number; z1: number } }[] {
    const out: { n: number; rect: { x0: number; z0: number; x1: number; z1: number } }[] = [];
    for (const d of this.live?.plots || []) {
      const p = this.byN.get(d.n), m = d.pavilion?.masses?.[0];
      if (!p || !m || p.ring === 0 || d.status !== 'owned') continue;
      const cx = m.x + m.w / 2, fz = m.y + m.d, f = frame(p);
      const a = new THREE.Vector3(cx - 0.75, 0, fz - 0.3).applyMatrix4(f), b = new THREE.Vector3(cx + 0.75, 0, fz + 0.8).applyMatrix4(f);
      out.push({ n: d.n, rect: { x0: Math.min(a.x, b.x), z0: Math.min(a.z, b.z), x1: Math.max(a.x, b.x), z1: Math.max(a.z, b.z) } });
    }
    return out;
  }
  // the land to walk on, within a box
  walkGrounds(b: { x0: number; z0: number; x1: number; z1: number }) {
    return this.layout.grounds.map(([x0, z0, x1, z1]) => ({ x0: Math.max(x0, b.x0), z0: Math.max(z0, b.z0), x1: Math.min(x1, b.x1), z1: Math.min(z1, b.z1), y: 0 }))
      .filter((g) => g.x1 > g.x0 && g.z1 > g.z0);
  }
  bounds() {
    let x0 = 0, z0 = 0, x1 = 80, z1 = 56;
    for (const [a, b, c, d] of this.layout.grounds) { x0 = Math.min(x0, a); z0 = Math.min(z0, b); x1 = Math.max(x1, c); z1 = Math.max(z1, d); }
    return { x0, z0, x1, z1 };
  }

  // ----- the land, the paths, the squares and the signposts (built once, merged)

  private buildGround(g: THREE.Group) {
    // the same lawn and earth as the old island (borrowed from a small one, so the weather wets them alike)
    const src = island(0, 0, 4, 4, { seed: 40, depth: 2.6, stripes: false });
    const faces = (src.children[0] as THREE.Mesh).material as THREE.Material[];
    const rockM = mat('#86705c', { rough: 1, flat: true }), rock2 = mat('#76624f', { rough: 1, flat: true });
    const land = new THREE.Group();
    this.layout.grounds.forEach(([x0, z0, x1, z1], i) => {
      land.add(wbox(x0, -2.6, z0, x1, 0, z1, faces));
      // underneath, rock tapering down, a little ragged
      const w = x1 - x0, d = z1 - z0, H = Math.min(40, Math.min(w, d) * 0.55 + 6);
      for (const [k, s, hm, m] of [[0, 1, 1, rockM], [1, 0.62, 0.72, rock2]] as [number, number, number, THREE.Material][]) {
        const geo = new THREE.CylinderGeometry(Math.SQRT1_2, 0.12, 1, 4, 4).toNonIndexed();
        geo.rotateY(Math.PI / 4);
        const r = rng(500 + i * 7 + k), pa = geo.attributes.position, seen = new Map<string, [number, number, number]>();
        for (let j = 0; j < pa.count; j++) {
          const key = `${pa.getX(j).toFixed(3)},${pa.getY(j).toFixed(3)},${pa.getZ(j).toFixed(3)}`;
          if (!seen.has(key)) seen.set(key, [0.86 + r() * 0.22, (r() - 0.5) * 0.08, r()]);
          const [q, dy] = seen.get(key)!, top = pa.getY(j) > 0.49;
          pa.setXYZ(j, pa.getX(j) * (top ? 0.985 : q), pa.getY(j) + (top ? 0 : dy), pa.getZ(j) * (top ? 0.985 : q));
        }
        geo.computeVertexNormals();
        const cone = new THREE.Mesh(geo, m);
        cone.scale.set(w * s, H * hm, d * s);
        cone.position.set((x0 + x1) / 2 + (k ? w * 0.06 : 0), -2.6 - (H * hm) / 2 + 0.02, (z0 + z1) / 2 + (k ? -d * 0.05 : 0));
        cone.userData.noShadow = true;
        land.add(cone);
      }
    });
    splitMaterials(land);
    land.traverse((o: any) => { if (o.isMesh && o.material === faces[2]) o.userData.lawn = true; });
    g.add(land);
    // the homestead's stepping stones, off the far end of Homestead Road
    const { x0 } = this.bounds();
    for (let k = 1; k <= 5; k++) { const st = new THREE.Mesh(new THREE.SphereGeometry(0.9 + (k % 2) * 0.3, 7, 5), rockM); st.scale.y = 0.45; st.position.set(x0 - 3 - k * 3.4, -0.6 - k * 0.5, 31.6 + Math.sin(k) * 1.5); g.add(st); }
  }

  private buildPaths(g: THREE.Group) {
    const gravel = surf({ kind: 'dirt', color: '#d9c9a6' }), flag = surf({ kind: 'flagstone', color: '#d6ccba' });
    const lane = surf({ kind: 'dirt', color: '#e0d2b2' }), edge = surf({ kind: 'dirt', color: '#d4c39e' });
    for (const a of this.layout.avenues) {
      const [x0, z0, x1, z1] = a.rect, along = x1 - x0 > z1 - z0;
      // a path down the middle, grass verges either side for the trees
      const inset = a.kind === 'avenue' ? 1.3 : a.kind === 'walk' ? 0.8 : 0.6;
      const [px0, pz0, px1, pz1] = along ? [x0, z0 + inset, x1, z1 - inset] : [x0 + inset, z0, x1 - inset, z1];
      g.add(wbox(px0, 0.002, pz0, px1, 0.022, pz1, a.kind === 'walk' ? flag : a.kind === 'edge' ? edge : gravel));
    }
    for (const l of this.layout.lanes) {
      const [x0, z0, x1, z1] = l.rect, along = x1 - x0 > z1 - z0;
      const [px0, pz0, px1, pz1] = along ? [x0, z0 + 0.5, x1, z1 - 0.5] : [x0 + 0.5, z0, x1 - 0.5, z1];
      g.add(wbox(px0, 0.003, pz0, px1, 0.02, pz1, lane));
    }
    // two paths on the old island, from the plaza's cross paths up to the Plaza Ring
    for (const [x0, x1] of [[0.6, 3.2], [76.8, 79.4]]) g.add(wbox(x0, 0.003, 0, x1, 0.024, 30.7, flag));
    // round greens where the walks cross the ring avenues
    for (const c of this.layout.crossings) {
      const [x, z] = c.at;
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.4, 0.03, 32), flag); disc.position.set(x, 0.02, z); g.add(disc);
      const bed = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.6, 0.28, 24), surf({ kind: 'grass', color: '#7fa456' })); bed.position.set(x, 0.14, z); g.add(bed);
      this.extraTrees.push([x, z, 'cherry']);
      const r = rng(Math.round(x * 7 + z * 13) >>> 0), cols = ['#f2c14e', '#f6f0e6', '#e48aa0', '#b98ae8'];
      for (let i = 0; i < 18; i++) { const a = (i / 18) * Math.PI * 2 + r() * 0.2; const f = new THREE.Mesh(new THREE.SphereGeometry(0.11, 6, 4), mat(cols[i % 4])); f.position.set(x + Math.cos(a) * 1.25, 0.36, z + Math.sin(a) * 1.25); f.userData.noShadow = true; g.add(f); }
      this.lampSpots.push([x + 2.6, z + 2.6, 3]);
    }
  }

  private buildSquares(g: THREE.Group) {
    const flag = surf({ kind: 'flagstone', color: '#d9cfbd' });
    this.layout.squares.forEach((s, i) => {
      const [x0, z0, x1, z1] = s.rect, [cx, cz] = s.at;
      const w = x1 - x0, d = z1 - z0, along = w > d;
      // a little green: a paved ring round what stands in the middle, the lane running through
      const ring = new THREE.Mesh(new THREE.CylinderGeometry(3.6, 3.6, 0.03, 36), flag); ring.position.set(cx, 0.016, cz); g.add(ring);
      const f = squareFeature(s.kind, i + 3); f.position.set(cx, 0.03, cz); g.add(f);
      // flower borders along the edges that face the plots, benches facing in
      for (const sgn of [-1, 1]) {
        const bx = along ? cx : cx + sgn * (w / 2 - 1.3), bz = along ? cz + sgn * (d / 2 - 1.3) : cz;
        const border = bed(along ? w - 6 : 0.8, along ? 0.8 : d - 6, ['#e25d6a', '#f2c14e', '#f6f0e6', '#7aa7d9', '#e48aa0'], i * 3 + sgn);
        border.position.set(bx, 0, bz); g.add(border);
        // two benches each side of the ring, facing in
        for (const t of [-1, 1]) {
          const [bx2, bz2] = along ? [cx + t * 4.2, cz + sgn * 4.4] : [cx + sgn * 4.4, cz + t * 4.2];
          g.add(at(Y.gardenBench({}), bx2, 0, bz2, along ? (sgn > 0 ? 0 : Math.PI) : (sgn > 0 ? Math.PI / 2 : -Math.PI / 2)));
        }
      }
      // a tree at each corner
      for (const [tx, tz] of [[x0 + 1.6, z0 + 1.6], [x1 - 1.6, z0 + 1.6], [x0 + 1.6, z1 - 1.6], [x1 - 1.6, z1 - 1.6]]) this.extraTrees.push([tx, tz, i % 3 === 0 ? 'cherry' : i % 3 === 1 ? 'oak' : 'lime']);
      this.lampSpots.push([cx, cz, 4.5]);
    });
  }

  private buildSigns(g: THREE.Group) {
    // a fingerpost at each end of each lane, naming the quarter
    for (const s of this.layout.signs) {
      const ry = s.side === 'north' ? (s.dir > 0 ? Math.PI : 0) : (s.dir > 0 ? Math.PI / 2 : -Math.PI / 2);
      const f = fingerpost([{ text: s.text, ry }]);
      f.position.set(s.at[0], 0, s.at[1]); g.add(f);
    }
    // and at each crossing, which walk and which ring
    const ringName: Record<number, string> = { 1: 'the Plaza Ring', 2: 'the Linden Ring', 3: 'the Rowan Ring', 4: 'the Edge Walk' };
    for (const c of this.layout.crossings) {
      const f = fingerpost([{ text: c.walk, ry: Math.PI / 2, small: true }, { text: ringName[c.ring], ry: 0, small: true }]);
      f.position.set(c.at[0] + 3.1, 0, c.at[1] - 3.1); g.add(f);
    }
  }

  // ----- trees, lamps and hedges: one instanced mesh a kind

  private extraTrees: [number, number, string][] = [];
  private crownRecs: { x: number; z: number; m: THREE.Matrix4[]; c: THREE.Color[] }[] = [];
  private crowns: THREE.InstancedMesh[] = [];
  private farDetail = new FarDetail();
  private bushRecs: { x: number; z: number; m: THREE.Matrix4[]; c: THREE.Color[] }[] = [];
  private bushes: THREE.InstancedMesh[] = [];
  private buildTrees() {
    const r = rng(77);
    const trunks: THREE.Matrix4[] = [], birchTrunks: THREE.Matrix4[] = [];
    const leaf: Record<string, string[]> = {
      lime: ['#6f9a4a', '#7aa652', '#668f45'], oak: ['#4f7a42', '#5a8548', '#47703c'], plane: ['#6a9550', '#749e58'],
      cherry: ['#f2b8c6', '#f5c6d2', '#eaa9bb'], apple: ['#7aa64e', '#86b05a'], birch: ['#a3c46a', '#9cbf5a', '#b0cd78'],
    };
    // trees in back gardens too, here and there, so the rows read as gardens and not as fields
    for (const p of this.layout.plots) {
      if (p.ring === 0) continue;
      const rr = rng(p.n * 17 + 3);
      if (rr() > 0.34) continue;
      const [w] = p.size, c = new THREE.Vector3(rr() < 0.5 ? 0.9 : w - 0.9, 0, 0.9).applyMatrix4(frame(p));
      this.extraTrees.push([c.x, c.z, ['apple', 'cherry', 'lime', 'birch', 'oak'][Math.floor(rr() * 5)]]);
    }
    for (const [x, z, kind] of [...this.layout.trees, ...this.extraTrees]) {
      const big = kind === 'oak' || kind === 'plane';
      const h = (big ? 2.4 : 1.9) + r() * 0.7, s = (big ? 1.45 : 1.1) + r() * 0.3;
      const c = new THREE.Color(leaf[kind]?.[Math.floor(r() * leaf[kind].length)] || '#6f9a4a');
      (kind === 'birch' ? birchTrunks : trunks).push(M(x, (h + s * 0.6) / 2, z, 1, h + s * 0.6, 1));
      // a crown of three soft blobs
      const blobs = kind === 'birch' ? [[0, 1.0, 0, 0.62, 1.15], [0.25, 1.75, 0.1, 0.45, 0.8]] : [[0, 0.62, 0, 1, 0.85], [0.45, 1.05, 0.2, 0.68, 0.62], [-0.4, 1.0, -0.3, 0.62, 0.58]];
      const rec = { x, z, m: [] as THREE.Matrix4[], c: [] as THREE.Color[] };
      for (const [bx, by, bz, bs, bh] of blobs) {
        rec.m.push(M(x + bx * s, h + by * s, z + bz * s, s * bs, s * bh, s * bs, r() * 6));
        rec.c.push(c.clone().offsetHSL(0, 0, (r() - 0.5) * 0.06));
      }
      this.crownRecs.push(rec);
    }
    const trunkGeo = new THREE.CylinderGeometry(0.1, 0.15, 1, 6);
    const g = new THREE.Group();
    g.add(inst(trunkGeo, mat('#6b4a33', { rough: 0.9 }), trunks, undefined, { shadow: true }));
    g.add(inst(trunkGeo, mat('#ece6da', { rough: 0.8 }), birchTrunks, undefined, { shadow: true }));
    // the crowns: finer (and casting shadows) near where the camera looks, coarser further off (fillDetail)
    const leafM = new THREE.MeshStandardMaterial({ roughness: 0.92, vertexColors: true, flatShading: true });
    const n = this.crownRecs.reduce((a, rr) => a + rr.m.length, 0);
    // (P83) and past a third of the view distance, one coarse blob a tree (fillDetail)
    this.crowns = [blobGeo(2), blobGeo(1), blobGeo(0)].map((geo, i) => {
      const im = new THREE.InstancedMesh(geo, leafM, i === 2 ? this.crownRecs.length : n);
      im.count = 0; im.castShadow = i === 0; im.receiveShadow = true; im.frustumCulled = false; im.userData.leaf = true;
      if (i) im.userData.noShadow = true;
      g.add(im); return im;
    });
    this.root.add(g);
  }

  private buildLamps() {
    const posts: THREE.Matrix4[] = [], heads: THREE.Matrix4[] = [];
    for (const [x, z] of this.layout.lamps) { posts.push(M(x, 1.3, z)); heads.push(M(x, 2.75, z)); this.lampSpots.push([x, z, 3.2]); }
    const g = new THREE.Group();
    g.add(inst(new THREE.CylinderGeometry(0.06, 0.08, 2.6, 6), mat('#2b3a36', { rough: 0.5 }), posts, undefined, { shadow: true }));
    // warm and bright enough that the evening lights them with the plaza's lamps (daylight.glowLamps)
    g.add(inst(new THREE.SphereGeometry(0.2, 10, 8), mat('#ffe2b0', { emissive: '#ffb86b', emissiveIntensity: 1.6 }), heads));
    this.root.add(g);
  }

  // between back gardens, a row of round shrubs (with gaps), never twice in one place
  private buildHedges() {
    const seen = new Set<string>();
    const greens = ['#5a8a48', '#4f7a42', '#66944f', '#56834a'].map((c) => new THREE.Color(c));
    for (const p of this.layout.plots) {
      if (p.ring === 0) continue;
      const [w] = p.size, F = frame(p);
      const back = new THREE.Vector3(w / 2, 0, -0.5).applyMatrix4(F);
      const kb = `${back.x.toFixed(0)},${back.z.toFixed(0)}`;
      if (seen.has(kb)) continue;
      seen.add(kb);
      const r = rng(p.n * 7 + 1), rec = { x: back.x, z: back.z, m: [] as THREE.Matrix4[], c: [] as THREE.Color[] };
      this.bushRecs.push(rec);
      for (let x = 0.5; x < w + 0.4; x += 1.05) {
        if (r() < 0.12) continue;
        const s = 0.42 + r() * 0.2;
        rec.m.push(F.clone().multiply(M(x + (r() - 0.5) * 0.2, s * 0.6, -0.5 + (r() - 0.5) * 0.15, s, s * 0.9, s, r() * 6)));
        rec.c.push(greens[Math.floor(r() * greens.length)].clone());
      }
    }
    // (a walker goes round them, as round a hedge)
    const bm = new THREE.MeshStandardMaterial({ roughness: 1, vertexColors: true, flatShading: true });
    const n = this.bushRecs.reduce((a, rr) => a + rr.m.length, 0);
    this.bushes = [blobGeo(1), blobGeo(0)].map((geo) => {
      const im = new THREE.InstancedMesh(geo, bm, n);
      im.count = 0; im.receiveShadow = true; im.frustumCulled = false; im.userData.noShadow = true;
      this.root.add(im); return im;
    });
  }

  // ----- the lots: staked, signed and flowered by their status (rebuilt when that changes)

  update(live: PlotsLive | undefined) {
    if (!live) return;
    this.live = live;
    const key = JSON.stringify([live.open, live.open_quarters, live.glow_quarters, live.prices, live.plots.map((p) => [p.n, p.status, p.owner])]);
    if (key !== this.lotsKey) { this.lotsKey = key; this.buildLots(); chunkInstanced(this.lots); this.farDetail.add(this.lots); this.farDetail.frame(this.lastCam || new THREE.Camera(), true); }
    // the owned plots: rebuilt when what's on them changes
    const want = new Map(live.plots.filter((p) => p.status === 'owned' && (this.byN.get(p.n)?.ring || 0) > 0).map((p) => [p.n, p]));
    for (const [n, s] of this.ownedState) {
      const d = want.get(n);
      if (!d || JSON.stringify(d) !== JSON.stringify(s.data)) { if (s.near) this.owned.remove(s.near); this.owned.remove(s.far); this.ownedState.delete(n); }
    }
    for (const [n, d] of want) {
      if (this.ownedState.has(n)) continue;
      const p = this.byN.get(n)!, c = this.centre(n)!;
      const far = this.farOwned(p, d); this.owned.add(far);
      // how big the building is (its footprint's diagonal), for how big it looks (frame)
      const ms: any[] = d.pavilion?.masses || [];
      const size = ms.length ? Math.hypot(Math.max(...ms.map((m) => m.x + m.w)) - Math.min(...ms.map((m) => m.x)), Math.max(...ms.map((m) => m.y + m.d)) - Math.min(...ms.map((m) => m.y))) : Math.hypot(...p.size);
      this.ownedState.set(n, { near: null, far, data: d, at: new THREE.Vector3(c[0], 0, c[1]), size, shown: false });
    }
  }

  // Every lot's lawn is drawn always; its stakes, string, sign, flowers and tufts only near where
  // the camera looks (fillDetail), so zoomed out the district costs little more than its trees.
  private recs: { x: number; z: number; m: Record<string, THREE.Matrix4[]>; c: Record<string, THREE.Color[]> }[] = [];
  private pools: Record<string, THREE.InstancedMesh> = {};
  private detailAt = new THREE.Vector3(1e9, 0, 0);
  private detailR = 0;

  private buildLots() {
    this.lots.clear();
    this.recs = [];
    const lawns: THREE.Matrix4[] = [], lawnCols: THREE.Color[] = [], poles: THREE.Matrix4[] = [], pennants: THREE.Matrix4[] = [];
    const fcols = ['#f6f0e6', '#f2c14e', '#e48aa0', '#b98ae8', '#7aa7d9', '#f08f5a'].map((c) => new THREE.Color(c));
    const meadow = [['#b98ae8', '#c9a6e4', '#f6f0e6', '#9f7bd8'], ['#f2c14e', '#f6e27a', '#fbf6ee'], ['#e2445a', '#f08aa8', '#f6f0e6'], ['#6f9fe0', '#9cc0ef', '#fbf6ee'], ['#f6f0e6', '#f2c14e', '#e48aa0', '#b98ae8', '#7aa7d9']].map((p) => p.map((c) => new THREE.Color(c)));
    const cRes = new THREE.Color(RESERVED), cSale = new THREE.Color(FOR_SALE);
    const lawnRes = new THREE.Color('#8eb05c'), lawnSale = new THREE.Color('#a8c46a').lerp(new THREE.Color(FOR_SALE), 0.24); // mown, warmed toward the for-sale orange, to read from far off
    for (const p of this.layout.plots) {
      if (p.ring === 0) continue;
      const st = this.status(p.n);
      if (st === 'owned') continue;
      const [w, d] = p.size, F = frame(p), res = st === 'reserved', col = res ? cRes : cSale;
      const m: Record<string, THREE.Matrix4[]> = { stakes: [], tips: [], strings: [], flowers: [], tufts: [], posts: [], boards: [], reserved: [], inner: [], outer: [] };
      const c: Record<string, THREE.Color[]> = { tips: [], strings: [], flowers: [] };
      const corners: [number, number][] = [[0.3, 0.3], [w - 0.3, 0.3], [w - 0.3, d - 0.3], [0.3, d - 0.3]];
      for (const [x, z] of corners) { m.stakes.push(F.clone().multiply(M(x, 0.35, z))); m.tips.push(F.clone().multiply(M(x, 0.72, z))); c.tips.push(col); }
      // the string between the stakes
      for (const [[ax, az], [bx, bz]] of [[corners[0], corners[1]], [corners[1], corners[2]], [corners[2], corners[3]], [corners[3], corners[0]]]) {
        const len = Math.hypot(bx - ax, bz - az), ry = Math.atan2(-(bz - az), bx - ax);
        m.strings.push(F.clone().multiply(M((ax + bx) / 2, 0.6, (az + bz) / 2, len, 1, 1, ry))); c.strings.push(col);
      }
      // a for-sale lot is mown; a reserved one has gone to meadow, each quarter its own
      // (lavender here, buttercups there), in drifts, so from far off the inner rings read as a patchwork
      const pal = res ? meadow[this.quarterIndex(p.quarter) % meadow.length] : fcols;
      lawns.push(F.clone().multiply(M(w / 2, 0.012, d / 2, w - 0.5, 1, d - 0.5)));
      lawnCols.push(res ? lawnRes.clone().lerp(pal[0], 0.13) : lawnSale);
      const r = rng(p.n * 31 + 7);
      const drifts = res ? [0, 1, 2].map(() => [0.8 + r() * (w - 1.6), 0.8 + r() * (d - 2.2)]) : [];
      for (let i = 0; i < (res ? 34 : 6); i++) {
        const [cx, cz] = res ? drifts[i % 3] : [0.7 + r() * (w - 1.4), 0.6 + r() * (d - 2.0)];
        const x = Math.min(w - 0.4, Math.max(0.4, cx + (res ? (r() + r() - 1) * 1.8 : 0))), z = Math.min(d - 1.0, Math.max(0.4, cz + (res ? (r() + r() - 1) * 1.5 : 0)));
        const s = (res ? 1.1 : 0.8) + r() * 0.6;
        m.flowers.push(F.clone().multiply(M(x, 0.22 + r() * 0.12, z, s, s, s))); c.flowers.push(pal[Math.floor(r() * pal.length)]);
      }
      if (res) for (let i = 0; i < 16; i++) { const x = 0.6 + r() * (w - 1.2), z = 0.6 + r() * (d - 1.6), k = 0.7 + r() * 0.7; m.tufts.push(F.clone().multiply(M(x, 0.16 * k, z, k, k, k, r() * 6))); }
      // a little sign by the lane: RESERVED, or FOR SALE and the price
      for (const sx of [-0.45, 0.45]) m.posts.push(F.clone().multiply(M(w / 2 + sx, 0.55, d - 0.55)));
      m.boards.push(F.clone().multiply(M(w / 2, 1.0, d - 0.58, 1.25, 0.62, 0.06)));
      m[res ? 'reserved' : p.ring < 3 ? 'inner' : 'outer'].push(F.clone().multiply(M(w / 2, 1.0, d - 0.545)));
      // for sale: a tall pole with an orange pennant at the lane corner, drawn at every distance
      if (!res) { poles.push(F.clone().multiply(M(0.35, 2.75, d - 0.35, 1, 1, 1))); pennants.push(F.clone().multiply(M(0.35, 4.85, d - 0.35, 1, 1, 1, Math.PI / 4))); }
      const [x0, z0, x1, z1] = p.rect;
      this.recs.push({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, m, c });
    }
    const lm = inst(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ roughness: 1, polygonOffset: true, polygonOffsetFactor: -1 }), lawns, lawnCols, { noCollide: true });
    lm.userData.lawn = true; this.lots.add(lm);
    // a big board on each square of an open quarter, seen from across the district
    const open = new Set((this.live?.open_quarters || []).filter((id) => (this.layout.quarters.find((q) => q.id === id)?.ring ?? 0) < 4));
    for (const sq of this.layout.squares.filter((q) => open.has(q.quarter))) this.lots.add(saleBoard(this.quarterName(sq.quarter), this.live!.prices[this.layout.quarters.find((q) => q.id === sq.quarter)?.inner ? 'inner' : 'outer'], sq.at));
    if (poles.length) {
      const flag = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0.6, 0), new THREE.Vector3(0, -0.6, 0), new THREE.Vector3(2.6, 0, 0)]);
      flag.computeVertexNormals();
      this.lots.add(inst(new THREE.CylinderGeometry(0.07, 0.08, 5.5, 5), mat('#f4ede0', { rough: 0.7 }), poles, undefined, { noCollide: true }));
      this.lots.add(inst(flag, new THREE.MeshStandardMaterial({ color: FOR_SALE, emissive: FOR_SALE, emissiveIntensity: 0.35, roughness: 0.8, side: THREE.DoubleSide }), pennants, undefined, { noCollide: true }));
    }
    // the pools the details are drawn from, as many as could ever be needed
    const L = this.live!;
    const faceMat = (lines: { text: string; size: number; color: string }[]) => new THREE.MeshStandardMaterial({ map: letters(lines, 300, 150, '#fbf5e8'), roughness: 0.9 });
    const faceGeo = new THREE.PlaneGeometry(1.15, 0.54);
    const kinds: [string, THREE.BufferGeometry, THREE.Material, { shadow?: boolean; noCollide?: boolean }][] = [
      ['stakes', new THREE.CylinderGeometry(0.05, 0.05, 0.7, 5), mat('#c9a06a', { rough: 0.8 }), { shadow: true }],
      ['tips', new THREE.CylinderGeometry(0.06, 0.06, 0.12, 5), new THREE.MeshStandardMaterial({ roughness: 0.6 }), {}],
      ['strings', new THREE.BoxGeometry(1, 0.018, 0.018), new THREE.MeshBasicMaterial(), { noCollide: true }],
      ['flowers', new THREE.IcosahedronGeometry(0.08, 0), new THREE.MeshStandardMaterial({ roughness: 0.8 }), { noCollide: true }],
      ['tufts', new THREE.ConeGeometry(0.09, 0.34, 4), mat('#7f9f52', { rough: 1 }), { noCollide: true }],
      ['posts', new THREE.CylinderGeometry(0.04, 0.045, 1.1, 5), mat('#6e4b33', { rough: 0.8 }), { shadow: true }],
      ['boards', new THREE.BoxGeometry(1, 1, 1), mat('#6e4b33', { rough: 0.8 }), { shadow: true }],
      ['reserved', faceGeo, faceMat([{ text: 'RESERVED', size: 46, color: '#5a4fa8' }, { text: 'by the crown', size: 30, color: '#3a2a20' }]), {}],
      ['inner', faceGeo, faceMat([{ text: 'FOR SALE', size: 48, color: '#c8547e' }, { text: `${L.prices.inner} coins`, size: 32, color: '#3a2a20' }]), {}],
      ['outer', faceGeo, faceMat([{ text: 'FOR SALE', size: 48, color: '#c8547e' }, { text: `${L.prices.outer} coins`, size: 32, color: '#3a2a20' }]), {}],
    ];
    this.pools = {};
    for (const [k, geo, mt, o] of kinds) {
      const n = this.recs.reduce((a, r) => a + r.m[k].length, 0);
      const im = new THREE.InstancedMesh(geo, mt, Math.max(1, n));
      im.count = 0; im.castShadow = !!o.shadow; im.receiveShadow = true;
      if (!o.shadow) im.userData.noShadow = true;
      if (o.noCollide) im.userData.noCollide = true;
      im.frustumCulled = false;
      this.pools[k] = im; this.lots.add(im);
    }
    this.detailAt.set(1e9, 0, 0);
  }

  // the details of the lots within R of a spot; (P83) trees and hedges only within the reach and
  // (roughly) in view, and past a third of the view distance a tree is one coarse blob
  private fillDetail(x: number, z: number, R: number, cam: THREE.PerspectiveCamera | null, view: number, reach: number) {
    const fr = new THREE.Frustum(), sph = new THREE.Sphere(), cp = cam ? cam.position : new THREE.Vector3(x, 0, z);
    if (cam) { cam.updateMatrixWorld(); fr.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse)); }
    const one = new THREE.Matrix4(), sc = new THREE.Matrix4().makeScale(1.25, 1.2, 1.25);
    for (const [recs, pools, f] of [[this.crownRecs, this.crowns, 0.8], [this.bushRecs, this.bushes, 0.6]] as const) {
      const n = [0, 0, 0];
      for (const r of recs) {
        const d = Math.hypot(r.x - cp.x, r.z - cp.z);
        if (cam) {
          if (d > reach + 8) continue;
          // a margin round the frustum that grows with the distance, so a turn between refills shows no gap
          sph.center.set(r.x, 3, r.z); sph.radius = 8 + d * 0.35;
          if (!fr.intersectsSphere(sph)) continue;
        }
        let k = R > 0 && Math.abs(r.x - x) < R * f && Math.abs(r.z - z) < R * f ? 0 : 1;
        if (cam && d > view / 3) k = pools.length > 2 ? 2 : 1;
        const im = pools[k];
        if (k === 2) { one.multiplyMatrices(r.m[0], sc); im.setMatrixAt(n[k], one); im.setColorAt(n[k], r.c[0]); n[k]++; continue; }
        r.m.forEach((mm, j) => { im.setMatrixAt(n[k], mm); im.setColorAt(n[k], r.c[j]); n[k]++; });
      }
      for (const [k, im] of pools.entries()) { im.count = n[k]; im.instanceMatrix.needsUpdate = true; if (im.instanceColor) im.instanceColor.needsUpdate = true; }
    }
    const near = R > 0 ? this.recs.filter((r) => Math.abs(r.x - x) < R && Math.abs(r.z - z) < R) : [];
    // (P83) the scatter (flowers, tufts) thinned by the tier, like grass (quality.ts detail)
    const share = Q().detail;
    for (const [k, im] of Object.entries(this.pools)) {
      let i = 0;
      const thin = k === 'flowers' || k === 'tufts';
      for (const r of near) r.m[k].forEach((mm, j) => {
        if (thin && j >= Math.ceil(r.m[k].length * share)) return;
        im.setMatrixAt(i, mm); if (r.c[k]) im.setColorAt(i, r.c[k][j]); i++;
      });
      im.count = i;
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
    }
  }

  // ----- an owned plot: small on screen, baked; bigger, the real thing

  // what stands on an owned plot, near or far: its sign by the lane (the owner's name on its face
  // near, a pale board far off), its flag, the building and the things in its garden
  private ownedParts(p: DPlot, d: PlotsLive['plots'][number], g: THREE.Group, signFace: THREE.Material) {
    const [w, dd] = p.size, wood = mat('#6e4b33', { rough: 0.8 });
    const s = new THREE.Group();
    for (const sx of [-0.85, 0.85]) s.add(at(cyl(0.05, 0.06, 1.5, wood, 6), sx, 0.75, 0));
    s.add(wbox(-1.05, 0.7, -0.05, 1.05, 1.6, 0.05, wood));
    const face = new THREE.Mesh(new THREE.PlaneGeometry(2.0, 0.84), signFace);
    face.position.set(0, 1.15, 0.056); s.add(face);
    // (P88) to one side of the front, so the way to the building's door is clear
    s.position.set(Math.min(w / 2, 1.7), 0, dd - 0.45); g.add(s);
    const f = flagpole(PLAZMI_FLAG, { h: 3.2 }); f.position.set(w - 1, 0, dd - 1); g.add(f);
    if (d.pavilion) { g.add(buildHouse(d.pavilion, {})); g.add(primeExtras(d.pavilion.kind, d.pavilion, d.owner_name || '')); }
    if (d.yard?.length) {
      const h = { version: 0, plot: p.size, spec: d.pavilion || { masses: [] }, furniture: [], yard: d.yard, designs: d.designs || [], chores: {}, resident: { name: d.owner_name || '', color: '#fff', glow: '#fff' } } as unknown as Fu.House;
      g.add(Fu.yard(h));
    }
  }

  // (far off, the same plot baked into a few meshes, so the swap to the near one is hard to see:
  // specks and what's inside the walls left out (farplot.ts))
  private farOwned(p: DPlot, d: PlotsLive['plots'][number]) {
    const src = new THREE.Group(), [w, dd] = p.size;
    src.add(wbox(0.2, 0.004, 0.2, w - 0.2, 0.014, dd - 0.2, surf({ kind: 'grass', color: '#93b462', stripes: true })));
    this.ownedParts(p, d, src, mat('#dfe6d6', { rough: 0.9 }));
    // (what's within a building's walls and below its eaves can't be seen from far off)
    const pv = d.pavilion, T = pv?.wallT ?? 0.16, ins = T / 2 - 0.005, top = (m: any) => (pv?.floorH ?? 0.45) + (m.stories || 1) * (pv?.storyH ?? 2.75) - 0.05;
    const g = bakeFar(src, { inside: (b) => (pv?.masses || []).some((m: any) => !(m.level > 0) && b.min.x > m.x + ins && b.max.x < m.x + m.w - ins && b.min.z > m.y + ins && b.max.z < m.y + m.d - ins && b.max.y < top(m)) });
    g.applyMatrix4(frame(p));
    return g;
  }

  private nearOwned(p: DPlot, d: PlotsLive['plots'][number]) {
    const g = new THREE.Group(), [w, dd] = p.size;
    g.add(wbox(0.2, 0.004, 0.2, w - 0.2, 0.014, dd - 0.2, surf({ kind: 'grass', color: '#93b462', stripes: true })));
    const stake = mat('#c9a06a', { rough: 0.8 });
    for (const [x, z] of [[0.3, 0.3], [w - 0.3, 0.3], [w - 0.3, dd - 0.3], [0.3, dd - 0.3]]) g.add(at(cyl(0.05, 0.05, 0.7, stake, 6), x, 0.35, z));
    this.ownedParts(p, d, g, new THREE.MeshStandardMaterial({ map: letters(plotSignLines({ ...d, n: p.n }), 512, 216, '#eef5e6'), roughness: 0.9 }));
    splitMaterials(g);
    g.applyMatrix4(frame(p));
    finishShadows(g); mergeStatic(g);
    return g;
  }

  // ----- each frame: owned plots near the camera in full; the far plane and the fog with the zoom

  private lodAt = 0;
  private lastCam: THREE.PerspectiveCamera | null = null;
  private seenPos = new THREE.Vector3(1e9, 0, 0);
  private seenDir = new THREE.Vector3();
  private seenReach = 0;
  frame(cam: THREE.PerspectiveCamera, target: THREE.Vector3, scene: THREE.Scene, onChange?: () => void, walking = false) {
    const dist = cam.position.distanceTo(target);
    this.lastCam = cam;
    if (!walking) {
      // the near plane pushed out with the zoom: the camera is never closer to the ground than a
      // fifth of its distance (the tilt stops at 10° above level), so a twentieth keeps every roof
      // in view, and the depth buffer keeps lawns, paths and pools of lamplight apart even fully zoomed out
      const near = THREE.MathUtils.clamp(dist * 0.05, 1, 40);
      if (Math.abs(cam.near - near) > near * 0.1) { cam.near = near; cam.updateProjectionMatrix(); }
    }
    // (P83) the far plane and the fog at the view distance (viewdist.ts)
    const { view, reach } = applyView(cam, scene, walking ? 0 : dist);
    farGlow(); // (the far plots' windows light up with the near ones')
    const now = performance.now();
    if (now - this.lodAt < 300) return;
    this.lodAt = now;
    this.farDetail.frame(cam);
    // where the camera looks, on the ground (in walk mode the camera moves and the target doesn't)
    const look = new THREE.Vector3(), dir = new THREE.Vector3();
    cam.getWorldDirection(dir);
    const t = dir.y < -0.05 ? -cam.position.y / dir.y : 0;
    look.copy(cam.position).addScaledVector(dir, Math.min(t, 600)); look.y = 0;
    // (P83) no further than ~2/5 of the view distance: the lots' small things are left out before the fog
    const R = dist > 420 ? 0 : Math.min(200, 45 + dist * 0.55, view * 0.4);
    // (the trees and hedges follow the camera too: where it is, which way it looks, how far it sees)
    const moved = cam.position.distanceTo(this.seenPos) > Math.max(3, dist * 0.04) || dir.dot(this.seenDir) < 0.996 || Math.abs(reach - this.seenReach) > reach * 0.05;
    if (moved || look.distanceTo(this.detailAt) > 14 || Math.abs(R - this.detailR) > 25 || (R === 0) !== (this.detailR === 0)) {
      this.detailAt.copy(look); this.detailR = R;
      this.seenPos.copy(cam.position); this.seenDir.copy(dir); this.seenReach = reach;
      this.fillDetail(look.x, look.z, R, cam, view, reach);
    }
    // (an owned plot in full while its building looks big enough on screen to make out its windows and
    // trim; smaller, the baked twin (farOwned), which looks the same. By its size on screen, not the
    // distance: zoomed out and tilted, the camera is far off while a plot still fills a good part of the view.)
    const perM = (innerHeight * cam.zoom) / (2 * Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2)); // pixels a metre covers 1 m away
    const T = NEAR_PX / Q().lod; // lower tiers swap a little sooner (quality.ts)
    let changed = false;
    for (const [n, s] of this.ownedState) {
      const px = (s.size / Math.max(1, cam.position.distanceTo(s.at))) * perM;
      let near = s.shown ? px > T / NEAR_BAND : px > T * NEAR_BAND; // a band either side, so it doesn't flicker at the edge
      if (this.lodForce) near = this.lodForce === 'near';
      if (near && !s.near) { s.near = this.nearOwned(this.byN.get(n)!, s.data); this.owned.add(s.near); }
      if (near !== s.shown) changed = true; // (the shadows are drawn again when one swaps)
      s.shown = near;
      if (s.near) s.near.visible = near;
      s.far.visible = !near;
    }
    if (changed) onChange?.();
  }
}
